import { Image } from '@accedo/ctv-image';
import { stylesheet } from 'astroturf';
import { FunctionalComponent } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';
import PlayIcon from '##/assets/icons/icon-Play.svg';
import { expiryToString } from '##/utils/DateTimeHelpers';
import { StandardCarouselItem } from '../StandardCarousel/types';

const styles = stylesheet`
  @import '##/styles/import.scss';

  .duration-overlay {
    position: absolute;
    bottom: pxToVh(7);
    right: 5px;
    padding: 5px;
    font-size: 1.25rem;
    font-weight: 400;
    background-color: rgba(0, 0, 0, 0.6);
  }

  .focusIconContainer {
    position: absolute;
    display:flex;
    align-items: center;
    justify-content: center;
    top: pxToVw(6);
    height: 100%;
    width: 100%;
    clip: rect(0,0,0,0);

    & svg {
      height: pxToVh(80);
      width: pxToVh(80);
    }

    .featured-play-button {
      height: pxToVw(190);
      width: pxToVw(200);
    }
  }

  .carousel-card-container {
    margin-bottom: pxToVh(6);
    display: inline-block;
    position: relative;
  }
  .upnext-card-container {
    margin-bottom: pxToVh(6);
    display: inline-block;
    position: relative;


  }

  .image {
    background-color: $primary-grey-30;
    object-fit: cover;
  }

  .expiryTag {
    top: 0;
    position: absolute;
    left: 0;
    background-color: red;
    font-size: pxToVh(13);
    padding: pxToVh(4.5) pxToVw(6);
  }

  :global(.current-focus) .upnext-card-container {
    border-radius: 2px;
    box-shadow: 0 0 0 2px $pure-white;
    .focusIconContainer {
      clip: auto;
    }
  }

  :global(.current-focus) .carousel-card-container {
    border-radius: 2px;
    transform: scale(1.1, 1.1);
    box-shadow: 0 0 0 2px $pure-white;
     
    .focusIconContainer {
      clip: auto;
    }

    .image {
      border-radius: 2px;
    }
  }

  .progress-bar-back {
    margin: 0;
    position:absolute;
    width: 100%;
    bottom: 0;
    height: pxToVw(4);
    border-bottom-left-radius: 2px;
    border-bottom-right-radius: 2px;
    background-color: rgba(233, 233, 233, 0.3);
    
    .progress-bar {
      height: pxToVw(4);
      border-bottom-left-radius: 2px;
      border-bottom-right-radius: 2px;
      background-color: $ten-play-blue;
    }
  }
`;

export const StandardCard: FunctionalComponent<StandardCarouselItem & {
  tileWidthPx: number;
  tileHeightPx?: number;
}> = (props) => {
  const [progress, setProgress] = useState<number | null>(null);

  const fallbackUrl = '/assets/placeholders/Fallback-Thumbnail-Landscape.jpg';
  const heightGivenWidth = (width: number): number =>
    Math.floor((width * 720) / 1280);

  const secondsToMinuteString = (seconds: number): string => {
    if (seconds >= 60) return `${Math.floor(seconds / 60)} mins`;
    return '< 1 min';
  };

  const progressToPercent = (progMs: number): number =>
    Math.min(100, 100 * (progMs / 1000 / props.duration));

  const getExpiryTag = useCallback(() => {
    const diff = props.expiry - Date.now() / 1000;
    const expiresWithinAWeek = diff > 0 && diff < 60 * 60 * 24 * 7;
    if (expiresWithinAWeek) {
      const expiryStr = expiryToString(props.expiry);
      return (
        <div className={styles.expiryTag}>
          {`Expires ${expiryStr}`.toUpperCase()}
        </div>
      );
    }
    return <></>;
  }, [props.expiry]);

  useEffect(() => {
    if (props.progress) {
      setProgress(progressToPercent(props.progress));
    }
  }, []);

  let cardImageUrl = fallbackUrl;
  if (props.imageUrl) {
    cardImageUrl =
      props.imageUrl && props.functionType === 'featured-episode'
        ? props.imageUrl
        : `${props.imageUrl}?image-profile=foxtel_landscape`;
  }

  return (
    <div
      className={
        props.functionType === 'featured-episode'
          ? styles.upnextCardContainer
          : styles.carouselCardContainer
      }
      style={{
        width: `${props.tileWidthPx}px`,
        height: props.tileHeightPx
          ? `${props.tileHeightPx}px`
          : `${heightGivenWidth(props.tileWidthPx)}px`,
      }}
    >
      <Image
        className={styles.image}
        url={cardImageUrl}
        fallbackUrl={fallbackUrl}
        width={props.tileWidthPx}
        height={
          props.tileHeightPx
            ? props.tileHeightPx
            : heightGivenWidth(props.tileWidthPx)
        }
      />
      <div className={styles.focusIconContainer}>
        <PlayIcon
          className={
            props.functionType === 'featured-episode'
              ? styles.featuredPlayButton
              : ''
          }
        />
      </div>
      {(progress && (
        <div className={styles.progressBarBack}>
          <div
            className={styles.progressBar}
            style={{
              width: `${progress}%`,
            }}
          />
        </div>
      )) || <></>}
      {props.functionType !== 'featured-episode' && getExpiryTag()}
      {(props.duration && (
        <div className={styles.durationOverlay}>
          {secondsToMinuteString(props.duration)}
        </div>
      )) || <></>}
    </div>
  );
};
